import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaUndo, FaGlobe, FaUser, FaCog, FaBell, FaLock } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site_name: '',
    site_description: '',
    availability_status: 'available',
    location: '',
    timezone: '',
    response_time: '',
    email_notifications: true,
    two_factor_auth: false,
    maintenance_mode: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiService.getSettings();
      setSettings(response.data || {
        site_name: 'Personal Engineering Platform',
        site_description: 'Full Stack Software Engineer Portfolio',
        availability_status: 'available',
        location: 'Kaduna, Nigeria',
        timezone: 'GMT+1',
        response_time: '< 24 Hours',
        email_notifications: true,
        two_factor_auth: false,
        maintenance_mode: false
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiService.updateSettings(settings);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset changes?')) {
      fetchSettings();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Manage your portfolio settings</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Settings */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaGlobe className="text-primary-400" />
            General Settings
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Site Name
              </label>
              <input
                type="text"
                value={settings.site_name || ''}
                onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Site Description
              </label>
              <textarea
                rows="2"
                value={settings.site_description || ''}
                onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Status Settings */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaUser className="text-primary-400" />
            Status Settings
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Availability Status
              </label>
              <select
                value={settings.availability_status || 'available'}
                onChange={(e) => setSettings({ ...settings, availability_status: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                value={settings.location || ''}
                onChange={(e) => setSettings({ ...settings, location: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Kaduna, Nigeria"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Timezone
              </label>
              <input
                type="text"
                value={settings.timezone || ''}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="GMT+1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Response Time
              </label>
              <input
                type="text"
                value={settings.response_time || ''}
                onChange={(e) => setSettings({ ...settings, response_time: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="< 24 Hours"
              />
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaBell className="text-primary-400" />
            Notification Settings
          </h3>

          <div className="space-y-3">
            <label className="flex items-center gap-3 text-gray-300">
              <input
                type="checkbox"
                checked={settings.email_notifications}
                onChange={(e) => setSettings({ ...settings, email_notifications: e.target.checked })}
                className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
              />
              Enable Email Notifications
            </label>
          </div>
        </div>

        {/* Security Settings */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaLock className="text-primary-400" />
            Security Settings
          </h3>

          <div className="space-y-3">
            <label className="flex items-center gap-3 text-gray-300">
              <input
                type="checkbox"
                checked={settings.two_factor_auth}
                onChange={(e) => setSettings({ ...settings, two_factor_auth: e.target.checked })}
                className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
              />
              Enable Two-Factor Authentication
            </label>

            <label className="flex items-center gap-3 text-gray-300">
              <input
                type="checkbox"
                checked={settings.maintenance_mode}
                onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })}
                className="w-4 h-4 rounded border-white/10 bg-white/5 text-primary-500 focus:ring-primary-500"
              />
              Maintenance Mode (Site will show maintenance page)
            </label>
          </div>
        </div>

        <div className="flex gap-4 pt-4 border-t border-white/10">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-6 py-2 border border-white/10 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
          >
            <FaUndo />
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;